import {
  GetUserMetricsDocument,
  useCustomCouponCodeMutation,
} from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/base/dialog';
import { FormField } from '@/components/base/form/form-field';
import { Input } from '@/components/base/form/input';
import { Loading } from '@/components/base/loading';
import { PageHeading } from '@/components/base/page-heading';
import { cn } from '@/utils';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from 'notistack';
import { useState, type FormEvent, type PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface EmailCodeComponentProps {
  onOpenChange: (value: boolean) => void;
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
}

export function RequestCouponModal({
  onOpenChange,
  isOpen,
  children,
  className,
}: PropsWithChildren<EmailCodeComponentProps>): JSX.Element {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [codeAvailable, setCodeAvailable] = useState(false);
  const [couponCodeLoading, setCouponCodeLoading] = useState(false);
  const [customCoupon] = useCustomCouponCodeMutation();

  const CouponSchema = z.object({
    newPassword: z.string().optional(),
    couponCode: z.string().min(1, {
      message: t('Coupon code is required'),
    }),
  });

  type Coupon = z.infer<typeof CouponSchema>;
  const methods = useForm<Coupon>({
    resolver: zodResolver(CouponSchema),
    mode: 'onChange',
  });
  const { handleSubmit, setError } = methods;

  async function checkCouponCodeAvaliable(): Promise<boolean> {
    setCouponCodeLoading(true);
    let available = false;
    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
    available = true;

    if (available) {
      setCodeAvailable(true);
      setCouponCodeLoading(false);
      return true;
    }
    setCodeAvailable(false);
    setCouponCodeLoading(false);
    return false;
  }

  // const debouncedOnChange = useCallback(() => {
  //   const timeout = setTimeout(() => {
  //     void checkUsernameAvaliable();
  //   }, 1000);
  //   return () => {
  //     clearTimeout(timeout);
  //   };
  // }, [watchUsername]);

  // useEffect(() => {
  //   return debouncedOnChange();
  // }, [watchUsername, debouncedOnChange]);

  async function save(data: z.infer<typeof CouponSchema>): Promise<void> {
    const { couponCode } = data;

    const currentUsernameAvailable = await checkCouponCodeAvaliable();
    if (currentUsernameAvailable) {
      try {
        await customCoupon({
          variables: {
            couponCodeInput: {
              couponCode,
            },
          },
          refetchQueries: [{ query: GetUserMetricsDocument }],
        });
        onOpenChange(false);
        enqueueSnackbar({
          message: t('Saved'),
          variant: 'success',
        });
      } catch (e) {
        if (e instanceof Error) {
          enqueueSnackbar({
            message: e.message,
            variant: 'error',
          });
        }
      }
    } else {
      setError('couponCode', { message: 'Coupon code is not available' });
    }
  }

  function handleClickSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      await handleSubmit(save)();
    })();
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value) => {
        onOpenChange(value);
      }}
    >
      <DialogTrigger
        asChild
        onClick={() => {
          onOpenChange(true);
        }}
      >
        <span
          className={cn(
            'h-full w-full cursor-pointer text-sm font-normal',
            className,
          )}
        >
          {children}
        </span>
      </DialogTrigger>
      <DialogContent className="max-w-3xl gap-6 md:pb-0">
        <div className="mx-auto flex w-full flex-col">
          <div className="mb-2 text-start">
            <PageHeading subheading={t('requestCouponModal.subheading')}>
              {t('requestCouponModal.title')}
            </PageHeading>
          </div>
          <div>
            <FormProvider {...methods}>
              <form
                onSubmit={(e) => {
                  handleClickSave(e);
                }}
              >
                <div className="flex h-full flex-col items-end justify-between gap-5 py-5 sm:flex-row">
                  <div className="flex h-full w-full">
                    <fieldset className="w-full">
                      <FormField
                        labelClassName="dark:text-white text-default-gray-950"
                        label={t('requestCouponModal.code')}
                        name="couponCode"
                      >
                        <Input
                          data-testid="firstName"
                          variant="secondary"
                          placeholder={t(
                            'requestCouponModal.couponPlaceholder',
                          )}
                          className="w-full"
                          icon={
                            <div className="flex items-center justify-center px-2">
                              {couponCodeLoading ? (
                                <Loading size="small" />
                              ) : (
                                codeAvailable && (
                                  <FontAwesomeIcon
                                    className="text-primary-positive transition-all duration-200"
                                    fixedWidth
                                    icon={faCircleCheck}
                                  />
                                )
                              )}
                            </div>
                          }
                        />
                      </FormField>
                    </fieldset>
                  </div>

                  <div className="flex w-full items-center justify-end sm:w-fit">
                    <Button type="submit">{t('Save')}</Button>
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
